/**
 * Zweck: Beschreibt die TypeScript-Bausteine für den Frontend-Bereich frontend/src/app/modules/shop/components/dtp-zusatzdaten-field-editor/shop.dtp-zusatzdaten-field-editor.component.ts.
 */
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {WarenkorbEntryDtpZusatzdaten} from '../../../../models/warenkorb-entry-dtp-zusatzdaten';
import {BehaviorSubject} from 'rxjs';
import {AppWarenkorbService} from '../../../../services/app.warenkorb.service';
import {AppDialogsService} from '../../../../services/app.dialogs.service';
import {AppAsyncTimeout} from '../../../../helpers/app.asyncTimeout';
import {HttpEventType} from '@angular/common/http';
import {AppDtpOrderService} from '../../../../services/app.dtp-order.service';
import {DtpZusatzdatentypenBlackeningConfigBox} from '../../../../models/dtp-zusatzdatentypen-blackening-config-box';
import {PDFDocument, rgb} from 'pdf-lib';
import {BestellungEntryDtpZusatzdaten} from '../../../../models/bestellung-entry-dtp-zusatzdaten';
import {finalize} from 'rxjs/operators';

/**
 * Zweck: Beschreibt class ShopDtpZusatzdatenFieldEditorComponent und ihren Daten- oder Funktionsvertrag.
 */
@Component({
  selector: 'shop-dtp-zusatzdaten-field-editor',
  templateUrl: 'shop.dtp-zusatzdaten-field-editor.component.html',
  styleUrls: ['shop.dtp-zusatzdaten-field-editor.component.scss'],
  standalone: false,
})
export class ShopDtpZusatzdatenFieldEditorComponent implements OnInit {
  @Input() warenkorbId: number;
  @Input() bestellungId: number;
  @Input() zusatzdaten: WarenkorbEntryDtpZusatzdaten|BestellungEntryDtpZusatzdaten;
  @Input() disabled = false;
  @Input() editMode = false;
  @Output() fieldChanged = new EventEmitter();

  loading = new BehaviorSubject(false);

  dirty = new BehaviorSubject(false);

  validationError = new BehaviorSubject<string>(undefined);

  showSuccess = new BehaviorSubject(false);
  showError = new BehaviorSubject(false);

  uploadPercentage = new BehaviorSubject<number>(undefined);

  blackeningConfigBoxes = new BehaviorSubject<DtpZusatzdatentypenBlackeningConfigBox[]>(null);

/**
 * Beschreibt Methode constructor und ihren Rückgabevertrag.
 */
  constructor(private svc: AppWarenkorbService, private dtpsvc: AppDtpOrderService, private dsvc: AppDialogsService) {
  }

/**
 * Beschreibt Methode ngOnInit und ihren Rückgabevertrag.
 */
  ngOnInit(): void {
    if (this.zusatzdaten.zusatzdaten.PdfGeschwaerzt) {
      if (this.blackeningConfigBoxes.value == null) {
        this.dtpsvc.getDtpZusatzdatentypenBlackeningConfigBoxesByMatchcode(this.zusatzdaten.zusatzdaten.Matchcode).subscribe(boxes => {
          this.blackeningConfigBoxes.next(boxes);
        });
      }
    }
  }

/**
 * Beschreibt Methode dsgvoManualBlackeningChanged und ihren Rückgabevertrag.
 */
  dsgvoManualBlackeningChanged() {
    if (this.zusatzdaten.file) {
      this.zusatzdaten.file = undefined;
      this.clear();
    }
  }

/**
 * Beschreibt Methode changeAutoBlackening und ihren Rückgabevertrag.
 */
  changeAutoBlackening() {
    if (this.zusatzdaten.file) {
      this.zusatzdaten.file = undefined;
    }

    this.zusatzdaten.dsgvoManualBlackening = false;

    if (this.zusatzdaten.file) {
      this.clear();
    }
  }

/**
 * Beschreibt Methode doShowError und ihren Rückgabevertrag.
 */
  doShowError() {
    this.showError.next(true);

    AppAsyncTimeout.setTimeout(() => {
      this.showError.next(false);
    }, 1500);
  }

/**
 * Beschreibt Methode doShowSuccess und ihren Rückgabevertrag.
 */
  doShowSuccess() {
    this.showSuccess.next(true);

    AppAsyncTimeout.setTimeout(() => {
      this.showSuccess.next(false);
    }, 1500);
  }

/**
 * Beschreibt Methode setDirty und ihren Rückgabevertrag.
 */
  setDirty() {
    if (this.dirty.value === false) {
      this.dirty.next(true);
    }
  }

/**
 * Beschreibt Methode clear und ihren Rückgabevertrag.
 */
  clear() {
    let currentDsgvoManualBlackening = this.zusatzdaten.dsgvoManualBlackening;
    this.loading.next(true);
    if (this.warenkorbId) {
      this.svc.clearDTPZusatzdatenData(this.warenkorbId, this.zusatzdaten.id).subscribe(ret => {
        ret.dsgvoManualBlackening = currentDsgvoManualBlackening;
        this.zusatzdaten = ret;
        this.loading.next(false);
        this.dirty.next(false);
      });
    } else if (this.bestellungId) {
      this.dtpsvc.clearCorrectedDTPZusatzdatenData(this.warenkorbId, this.zusatzdaten.lastError.id).subscribe(ret => {
        ret.dsgvoManualBlackening = currentDsgvoManualBlackening;
        this.zusatzdaten = ret;
        this.loading.next(false);
        this.dirty.next(false);
      });
    }
  }

/**
 * Zeigt die bereits im Warenkorb gespeicherte DTP-Datei an.
 */
  preview() {
    if (!this.warenkorbId || !this.zusatzdaten?.file?.id) {
      return;
    }

    this.loading.next(true);
    this.svc.getDTPZusatzdatenFile(this.warenkorbId, this.zusatzdaten.id)
      .pipe(finalize(() => this.loading.next(false)))
      .subscribe(blob => this.dsvc.previewFile(this.zusatzdaten.file, blob));
  }

/**
 * Beschreibt Methode executeBlackening und ihren Rückgabevertrag.
 */
  executeBlackening() {
    if (this.zusatzdaten.zusatzdaten.PdfGeschwaerzt && this.blackeningConfigBoxes.value.length > 0) {
      this.zusatzdaten.file.rawFile.arrayBuffer().then(buffer => {
        PDFDocument.load(buffer).then(pdfDoc => {
          const pages = pdfDoc.getPages();

          this.blackeningConfigBoxes.value.forEach(box => {
            const pageIdx = box.pageNum - 1;
            if (pages[pageIdx]) {
              pages[pageIdx].drawRectangle({
                x: box.x,
                y: box.y,
                width: box.width,
                color: rgb(0, 0, 0),
                height: box.height,
                opacity: 1,
                borderWidth: 1,
                borderColor: rgb(0, 0, 0)
              });
            }
          });

          pdfDoc.save().then(pdfBytes => {
            this.zusatzdaten.file.rawFile = new File([pdfBytes.buffer as ArrayBuffer], this.zusatzdaten.file.rawFile.name, {type: 'application/pdf'});
            /* EGGERD ZUM TESTEN
            var a = document.createElement("a");
            let url = window.URL.createObjectURL(this.zusatzdaten.file.rawFile);
            a.href = url;
            a.download = 'test.pdf';
            document.body.appendChild(a);
            a.click();*/
          });
        });
      });
    }
  }

/**
 * Beschreibt Methode delayedSave und ihren Rückgabevertrag.
 */
  delayedSave() {
    setTimeout(() => {
      this.save();
    });
  }

/**
 * Beschreibt Methode save und ihren Rückgabevertrag.
 */
  save() {
    if (this.warenkorbId) {
      if (this.zusatzdaten.data) {
        this.loading.next(true);
        this.svc.saveDTPZusatzdatenData(this.warenkorbId, this.zusatzdaten.id, this.zusatzdaten.data).subscribe(ret => {
          this.zusatzdaten = ret;
          if (ret.validationError) {
            this.validationError.next(ret.validationError);
            this.doShowError();
          } else {
            this.validationError.next(undefined);
            this.doShowSuccess();
          }
          this.loading.next(false);
          this.dirty.next(false);
          this.fieldChanged.emit();
        });

      } else if (this.zusatzdaten.file) {
        if (this.zusatzdaten.dsgvoAutoBlackening) {
          this.executeBlackening();
        }

        this.svc.uploadDTPZusatezdatenFile(this.warenkorbId, this.zusatzdaten.id, this.zusatzdaten.file, this.zusatzdaten.dsgvoAutoBlackening, this.zusatzdaten.dsgvoManualBlackening).subscribe(ev => {
          if (this.zusatzdaten.file && ev.type == HttpEventType.Sent) {
            this.uploadPercentage.next(0);

          } else if (this.zusatzdaten.file && ev.type == HttpEventType.UploadProgress) {
            const percentDone = Math.round(100 * ev.loaded / ev.total);
            this.uploadPercentage.next(percentDone);

          } else if (ev.type == HttpEventType.Response) {
            this.uploadPercentage.next(null);
            const body = ev.body;
            this.zusatzdaten = body;
            this.fieldChanged.emit();
          }
        });
      }

    } else if (this.bestellungId) {
      if (this.zusatzdaten.data) {
        this.loading.next(true);
        this.dtpsvc.saveCorrectedDTPZusatzdatenData(this.bestellungId, this.zusatzdaten.lastError.id, this.zusatzdaten.data).subscribe(ret => {
          this.zusatzdaten = ret;
          if (ret.validationError) {
            this.validationError.next(ret.validationError);
            this.doShowError();
          } else {
            this.validationError.next(undefined);
            this.doShowSuccess();
          }
          this.loading.next(false);
          this.dirty.next(false);
          this.fieldChanged.emit();
        });

      } else if (this.zusatzdaten.file) {
        if (this.zusatzdaten.dsgvoAutoBlackening) {
          this.executeBlackening();
        }
        this.dtpsvc.uploadCorrectedDTPZusatezdatenFile(this.bestellungId, this.zusatzdaten.lastError.id, this.zusatzdaten.file).subscribe(ev => {
          if (this.zusatzdaten.file && ev.type == HttpEventType.Sent) {
            this.uploadPercentage.next(0);

          } else if (this.zusatzdaten.file && ev.type == HttpEventType.UploadProgress) {
            const percentDone = Math.round(100 * ev.loaded / ev.total);
            this.uploadPercentage.next(percentDone);

          } else if (ev.type == HttpEventType.Response) {
            this.uploadPercentage.next(null);
            const body = ev.body;
            this.fieldChanged.emit();
          }
        });
      }
    }

  }
}
